/**
 * RegulatoryProfileStep — Wizard step for regulatory body selection (DRR-075).
 *
 * Split layout:
 *   Left (col=7): Suggestion cards with accept/reject actions
 *   Right (col=5): Live constellation preview (updates as nodes are accepted)
 *
 * Two-phase suggestion:
 * 1. Deterministic matching runs instantly from NAICS + state
 * 2. "Enhance with AI" button triggers LLM enrichment (Phase 5-6)
 */

import React from 'react';
import {
  Alert,
  Badge,
  Button,
  Grid,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Text,
} from '@mantine/core';
import {
  IconBrain,
  IconCheck,
  IconRefresh,
  IconShieldCheck,
  IconSparkles,
} from '@tabler/icons-react';

import { RegulatoryConstellationGraph } from '@ferp/workflow-forms/scope';

import { RegulatorySuggestionCard } from '../components/RegulatorySuggestionCard';
import {
  useRegulatoryOnboarding,
  type RegulatoryNodeSuggestion,
  type UseRegulatoryOnboardingReturn,
} from '../hooks/useRegulatoryOnboarding';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface RegulatoryProfileStepProps {
  /** NAICS sector code from industry step. */
  readonly naicsSector?: string;
  /** NAICS subsector code from industry step. */
  readonly naicsSubsector?: string;
  /** US state code from location step. */
  readonly state?: string;
  /** Tenant ID for this setup. */
  readonly tenantId: string;
  /** Tenant display name. */
  readonly tenantName: string;
  /** Callback when accepted nodes change. */
  readonly onAcceptedChange: (accepted: readonly RegulatoryNodeSuggestion[]) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RegulatoryProfileStep({
  naicsSector,
  naicsSubsector,
  state,
  tenantId,
  tenantName,
  onAcceptedChange,
}: RegulatoryProfileStepProps) {
  const onboarding = useRegulatoryOnboarding({
    naicsSector,
    naicsSubsector,
    state,
    tenantId,
    tenantName: tenantName || 'New Tenant',
  });

  const {
    suggestions,
    accepted,
    rejected,
    pending,
    statuses,
    constellation,
    hasMatched,
    isLlmEnriching,
    error,
    acceptNode,
    rejectNode,
    undoNode,
    acceptAllHighConfidence,
    resetAll,
    enhanceWithLlm,
  } = onboarding;

  // Notify parent when accepted nodes change
  React.useEffect(() => {
    onAcceptedChange(accepted);
  }, [accepted, onAcceptedChange]);

  // No industry/state selected yet
  if (!naicsSector || !state) {
    return (
      <Alert icon={<IconShieldCheck size={16} />} color="blue" variant="light">
        <Text size="sm">
          Select your industry and state in the previous steps to see applicable
          regulatory bodies.
        </Text>
      </Alert>
    );
  }

  // Matching in progress
  if (!hasMatched) {
    return (
      <Stack align="center" gap="md" py="xl">
        <Loader size="md" />
        <Text size="sm" c="dimmed">
          Analyzing regulatory requirements...
        </Text>
      </Stack>
    );
  }

  // No suggestions found
  if (suggestions.length === 0) {
    return (
      <Alert icon={<IconShieldCheck size={16} />} color="gray" variant="light">
        <Text size="sm">
          No specific regulatory requirements identified for your industry and location.
          You can proceed or use &quot;Enhance with AI&quot; for a deeper analysis.
        </Text>
        <Button
          mt="sm"
          size="xs"
          variant="light"
          leftSection={<IconBrain size={14} />}
          loading={isLlmEnriching}
          onClick={enhanceWithLlm}
        >
          Enhance with AI
        </Button>
      </Alert>
    );
  }

  return (
    <Stack gap="md" py="md">
      {/* Header */}
      <Group justify="space-between">
        <Group gap="xs">
          <IconShieldCheck size={20} />
          <Text fw={600}>Regulatory Bodies</Text>
        </Group>
        <Group gap="xs">
          {pending.length > 0 && (
            <Badge color="blue" variant="light" size="sm">
              {pending.length} pending
            </Badge>
          )}
          <Badge color="green" variant="light" size="sm">
            {accepted.length} accepted
          </Badge>
          {rejected.length > 0 && (
            <Badge color="red" variant="light" size="sm">
              {rejected.length} rejected
            </Badge>
          )}
        </Group>
      </Group>

      {error && (
        <Alert color="red" variant="light">
          {error}
        </Alert>
      )}

      {/* Action buttons */}
      <Group gap="xs">
        <Button
          size="xs"
          variant="light"
          color="green"
          leftSection={<IconCheck size={14} />}
          onClick={acceptAllHighConfidence}
          disabled={pending.filter((s) => s.confidence >= 0.8).length === 0}
        >
          Accept All High-Confidence
        </Button>
        <Button
          size="xs"
          variant="light"
          color="violet"
          leftSection={<IconBrain size={14} />}
          loading={isLlmEnriching}
          onClick={enhanceWithLlm}
        >
          Enhance with AI
        </Button>
        {(accepted.length > 0 || rejected.length > 0) && (
          <Button
            size="xs"
            variant="subtle"
            color="gray"
            leftSection={<IconRefresh size={14} />}
            onClick={resetAll}
          >
            Reset All
          </Button>
        )}
      </Group>

      {/* Split layout: suggestions + constellation */}
      <Grid gutter="md">
        {/* Left: suggestion cards */}
        <Grid.Col span={7}>
          <ScrollArea h={420} type="auto">
            <Stack gap="xs">
              {/* Pending suggestions first */}
              {pending.map((suggestion) => (
                <RegulatorySuggestionCard
                  key={suggestion.nodeId}
                  suggestion={suggestion}
                  status={statuses[suggestion.nodeId] || 'pending'}
                  onAccept={acceptNode}
                  onReject={rejectNode}
                  onUndo={undoNode}
                />
              ))}

              {/* Accepted suggestions */}
              {accepted.length > 0 && pending.length > 0 && (
                <Text size="xs" fw={600} c="green" mt="xs">
                  Accepted ({accepted.length})
                </Text>
              )}
              {accepted.map((suggestion) => (
                <RegulatorySuggestionCard
                  key={suggestion.nodeId}
                  suggestion={suggestion}
                  status="accepted"
                  onAccept={acceptNode}
                  onReject={rejectNode}
                  onUndo={undoNode}
                />
              ))}

              {/* Rejected suggestions */}
              {rejected.length > 0 && (
                <Text size="xs" fw={600} c="red" mt="xs">
                  Rejected ({rejected.length})
                </Text>
              )}
              {rejected.map((suggestion) => (
                <RegulatorySuggestionCard
                  key={suggestion.nodeId}
                  suggestion={suggestion}
                  status="rejected"
                  onAccept={acceptNode}
                  onReject={rejectNode}
                  onUndo={undoNode}
                />
              ))}
            </Stack>
          </ScrollArea>
        </Grid.Col>

        {/* Right: live constellation preview */}
        <Grid.Col span={5}>
          {constellation ? (
            <RegulatoryConstellationGraph
              constellation={constellation}
              height={420}
            />
          ) : (
            <Paper
              h={420}
              withBorder
              style={{
                borderStyle: 'dashed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Stack align="center" gap="sm">
                <IconSparkles size={32} color="var(--mantine-color-gray-5)" />
                <Text size="sm" c="dimmed" ta="center" maw={200}>
                  Accept regulatory bodies to build your compliance constellation
                </Text>
              </Stack>
            </Paper>
          )}
        </Grid.Col>
      </Grid>

      {/* Summary note */}
      <Text size="xs" c="dimmed">
        Regulatory bodies determine which compliance rules, workflows, and audit
        requirements apply to your business. You can modify these later from the
        Scope Management page.
      </Text>
    </Stack>
  );
}
